import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { UserService } from '~/app/services/user/user.service';
import type { CreateOrganizationUserRequestDto } from '@evimo/types/api/user/create-organization-user/create-organization-user.request.dto';
import type { ListOrganizationUsersDto } from '@evimo/types/api/user/list-organization-users/list-organization-users.dto';
import { ManagersTabPageView } from '@/app/landing/admin/back-office/organizations/detail/managers/ManagersTabPageView';

export function ManagersTabPageContainer() {
  const { orgId } = useParams<{ orgId: string }>();
  useTranslation('organization');
  const [users, setUsers] = useState<ListOrganizationUsersDto[]>([]);
  const [loading, setLoading] = useState(false);

  const loadUsers = async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const data = await UserService.listOrganizationUsers(
        orgId,
        'ORGANIZATION_MANAGER',
        false,
      );
      setUsers(data);
    } catch (error) {
      console.error('Failed to load managers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (data: CreateOrganizationUserRequestDto) => {
    if (!orgId) return;
    try {
      await UserService.createOrganizationUser(orgId, {
        ...data,
        organizationId: orgId,
        role: 'ORGANIZATION_MANAGER',
      });
      await loadUsers();
    } catch (error) {
      console.error('Failed to create user:', error);
      throw error;
    }
  };

  const handleDeleteUser = async (id: string) => {
    if (!orgId) return;
    try {
      await UserService.deleteUser(orgId, id);
      await loadUsers();
    } catch (error) {
      console.error('Failed to delete user:', error);
      throw error;
    }
  };

  useEffect(() => {
    loadUsers();
  }, [orgId]);

  if (!orgId) return null;

  return (
    <ManagersTabPageView
      organizationId={orgId}
      users={users}
      loading={loading}
      onCreateUser={handleCreateUser}
      onDeleteUser={handleDeleteUser}
      onRefresh={loadUsers}
    />
  );
}
